import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AuthTokenResponse,
  ILoginApiResponse,
} from '@merchant-portal/app/models/login/login.interface';
import { IOtpSend } from '@merchant-portal/app/models/otp/otp.interface';
import { environment } from '@merchant-portal/environments/environment';
import { Observable, switchMap } from 'rxjs';
import { AuthService } from '../../auth.service';
import { TURNSTILE_ACTION } from '../../turnstile/constant/turnstile.constant';
import { TurnstileService } from '../../turnstile/turnstile.service';
import { OTP_EVENT_TYPE } from '../constant/event.type';

@Injectable({
  providedIn: 'root',
})
export class OtpApiService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private turnstileService = inject(TurnstileService);
  url = environment.backend_portal_url;

  verifyOtp(otp: string, token: string): Observable<ILoginApiResponse> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http.post<ILoginApiResponse>(
      `${this.url}api/v1/auth/otp/verify`,
      { otp },
      { headers }
    );
  }

  resendOtp(obj: IOtpSend): Observable<ILoginApiResponse> {
    const isForgotPassword = obj.event === OTP_EVENT_TYPE.FORGOT_PASSWORD;
    const turnstileAction = isForgotPassword
      ? TURNSTILE_ACTION.FORGOT_PASSWORD
      : TURNSTILE_ACTION.OTP_SEND;

    // Use explicit twoFactorAuthMethod if provided, otherwise use default logic
    let twoFactorAuthMethod = obj.twoFactorAuthMethod;

    if (!twoFactorAuthMethod) {
      const shouldAddTwoFactorAuth = [
        OTP_EVENT_TYPE.FORGOT_PIN,
        OTP_EVENT_TYPE.CHANGE_PASSWORD,
        OTP_EVENT_TYPE.USER_LOGIN,
      ].includes(obj.event as OTP_EVENT_TYPE);

      twoFactorAuthMethod = shouldAddTwoFactorAuth ? 'AUTO' : 'OTP';
    }

    const requestBody = { ...obj, twoFactorAuthMethod };

    return this.turnstileService.getHeaders(turnstileAction).pipe(
      switchMap(turnstileHeaders => {
        if (isForgotPassword) {
          return this.http.post<ILoginApiResponse>(
            `${this.url}api/v1/auth/forgot-password`,
            requestBody,
            { headers: new HttpHeaders(turnstileHeaders) }
          );
        }

        const headers = new HttpHeaders({
          Authorization: `Bearer ${this.authService.getTempToken()}`,
          'X-Access-Token': this.authService.getAccessToken() ?? '',
          ...turnstileHeaders,
        });
        return this.http.post<ILoginApiResponse>(`${this.url}api/v1/auth/otp/send`, requestBody, {
          headers,
        });
      })
    );
  }

  successVerify(
    token: string,
    ipInfo: string,
    isRemember: string,
    deviceId: string
  ): Observable<AuthTokenResponse> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'X-IP-Info': JSON.stringify(ipInfo),
      'X-Device-Identifier': deviceId,
      'X-Is-Remember': isRemember,
    });
    return this.http.get<AuthTokenResponse>(`${this.url}api/v1/users/2fa/token`, { headers });
  }
}
